<template><div><h2 id="headings-and-subheadings" tabindex="-1" class="d-docsite--header-2">Headings and Subheadings <a class="header-anchor d-link d-docsite--link d-link" href="#headings-and-subheadings" aria-hidden="true">#</a></h2>
<h3 id="basic-structure" tabindex="-1" class="d-docsite--header-3">Basic Structure <a class="header-anchor d-link d-docsite--link d-link" href="#basic-structure" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Headings and subheadings are titles and subtitles that refer to interface sections. These should be:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Informative and descriptive
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Highlight the most important concept or information for users</li>
<li class="d-docsite--list-element">Help users understand what they’ll find in the sections</li>
</ul>
</li>
<li class="d-docsite--list-element">Concise and scannable
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use simple, clear language</li>
<li class="d-docsite--list-element">Keep headings to a single sentence</li>
<li class="d-docsite--list-element">Avoid using punctuation such as periods, commas, or semicolons</li>
<li class="d-docsite--list-element">Write headlines in sentence casing, capitalizing the first word and proper nouns only</li>
</ul>
</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Start meeting</li>
<li class="d-docsite--list-element">Invite users</li>
<li class="d-docsite--list-element">Create Ai Playbook</li>
<li class="d-docsite--list-element">Port number</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Start a meeting</li>
<li class="d-docsite--list-element">Invite users to your Dialpad account</li>
<li class="d-docsite--list-element">Create ai playbook</li>
<li class="d-docsite--list-element">Port your Number to Dialpad</li>
</ul>
</template>
</dialtone-usage>
<h3 id="articles" tabindex="-1" class="d-docsite--header-3">Articles <a class="header-anchor d-link d-docsite--link d-link" href="#articles" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Using articles (“the”, “a”, “an”) in titles depends on the message.</p>
<h4 class="d-docsite--header-4">Conversational Headings</h4>
<p class="d-docsite--paragraph">For conversational areas in the product, like empty states, use articles. It makes the language more approachable and helps users understand potentially new, complex concepts.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Secure your account with multi-factor authentication</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Multi-factor authentication</li>
</ul>
</template>
</dialtone-usage>
<h4 class="d-docsite--header-4">Microcopy Headings</h4>
<p class="d-docsite--paragraph">For labels, titles, and microcopy, avoid using articles to keep content short and action-oriented. This increases readability and scannability, encouraging action.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Start meeting</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Start a new meeting</li>
<li class="d-docsite--list-element">Start new meeting</li>
<li class="d-docsite--list-element">Start a meeting</li>
</ul>
</template>
</dialtone-usage>
<h2 id="sentences" tabindex="-1" class="d-docsite--header-2">Sentences <a class="header-anchor d-link d-docsite--link d-link" href="#sentences" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Start sentences with imperative verbs when introducing actions that can be (or need to be) taken. This is especially important when introducing new concepts or features. Sentences should sound instructional and actionable. Don’t use permissive language such as “you can” or “you might consider”.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Select departments observing this holiday</li>
<li class="d-docsite--list-element">Ensure calls are correctly routed by selecting the best option below.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Select the departments that will observe your new holiday.</li>
<li class="d-docsite--list-element">Calls can be routed to the right team every time. Identify the best routing option below that fits your business needs.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Shorter sentences are preferred because they’re easier to scan and understand. Focus sentences on single ideas to help break them down. Use the active verb structure (subject + verb + object). Avoid complex constructions.</p>
<p class="d-docsite--paragraph">There are times when longer sentences might be appropriate for conveying complex information. In these instances, clearly state the information in a well-structured format, using the following principles:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid unnecessary long sentences</li>
<li class="d-docsite--list-element">Avoid subordinate clauses (e.g., “When a caller is redirected to voicemail”)</li>
<li class="d-docsite--list-element">Change up sentence lengths to provide better text rhythm</li>
<li class="d-docsite--list-element">Break longer sentences into shorter ones whenever possible</li>
</ul>
<h2 id="buttons" tabindex="-1" class="d-docsite--header-2">Buttons <a class="header-anchor d-link d-docsite--link d-link" href="#buttons" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Like headlines, buttons need to be clear and concise. Users should be able to anticipate what will happen when they press or tap the button. Never mislead someone by mislabeling a button. When creating button labels, use the following rules:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Always start with a strong verb that encourages action</li>
<li class="d-docsite--list-element">Labels should provide enough context for users</li>
<li class="d-docsite--list-element">Use a “verb” + “noun” formula except for common action scenarios (e.g., “Done”, “Close”, “Cancel”)</li>
<li class="d-docsite--list-element">Avoid unnecessary words and articles such as “the”, “an”, or “a”</li>
<li class="d-docsite--list-element">Buttons should always be written in title case</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Visit Help Center</li>
<li class="d-docsite--list-element">Schedule Meeting</li>
<li class="d-docsite--list-element">View Submission</li>
<li class="d-docsite--list-element">Create Holiday</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">View help center</li>
<li class="d-docsite--list-element">Schedule a meeting</li>
<li class="d-docsite--list-element">Submission</li>
<li class="d-docsite--list-element">Create new holiday</li>
</ul>
</template>
</dialtone-usage>
<h2 id="links" tabindex="-1" class="d-docsite--header-2">Links <a class="header-anchor d-link d-docsite--link d-link" href="#links" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Like buttons, links need to be clear and predictable. Users should be able to anticipate what will happen when they click on a link. Never mislead someone by mislabeling a link. Never use “click here” or “here” as link text.</p>
<h3 id="links-within-sentences" tabindex="-1" class="d-docsite--header-3">Links Within Sentences <a class="header-anchor d-link d-docsite--link d-link" href="#links-within-sentences" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When using links in sentences, avoid linking the entire sentence. Only link the text that describes the link. Whenever possible, keep link text to single terms or small phrases. This makes it easier for internationalization as word order may change with translations.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="https://help.dialpad.com/docs/virtual-backgrounds" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Virtual backgrounds</a> only work in the latest versions of Google Chrome.</li>
<li class="d-docsite--list-element">Add a <a href="https://help.dialpad.com/v1/docs/en/desk-phones-and-room-phones" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">desk phone</a> or forwarding number below.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Virtual backgrounds only work in the latest versions of Google Chrome. To learn more, <a href="https://help.dialpad.com/docs/virtual-backgrounds" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">click here</a>.</li>
<li class="d-docsite--list-element"><a href="https://help.dialpad.com/v1/docs/en/desk-phones-and-room-phones" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Add a desk phone</a> or forwarding number below.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="links-outside-sentences" tabindex="-1" class="d-docsite--header-3">Links Outside Sentences <a class="header-anchor d-link d-docsite--link d-link" href="#links-outside-sentences" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Links outside sentences should follow the same approach as buttons. They should only be punctuated with question marks.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="#links-outside-sentences" class="d-docsite--link d-link">Forgot password?</a></li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="#links-outside-sentences" class="d-docsite--link d-link">Forgot password</a></li>
</ul>
</template>
</dialtone-usage>
<h3 id="learn-more-links" tabindex="-1" class="d-docsite--header-3">“Learn More” Links <a class="header-anchor d-link d-docsite--link d-link" href="#learn-more-links" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When linking out to further documentation, link relevant keywords. Avoid adding additional sentences with “Learn more…” as it’s repetitive and takes up unnecessary space.</p>
<p class="d-docsite--paragraph">Only add a “Learn more…” sentence if the preceding sentence addresses multiple concepts, each of which could be linked to their own document. In that situation, pick the most relevant link and contextualize it with “Learn more…”</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Ensure SMS message delivery by <a href="https://help.dialpad.com/v1/docs/en/sms-registration-guide" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">registering your campaign</a>.</li>
<li class="d-docsite--list-element">Dialpad retains data until it is deleted, an account is canceled, or a retention policy is set up. Canceled accounts are deleted after 180 days. Learn more about Dialpad’s <a href="https://help.dialpad.com/v1/docs/en/data-retention-policy" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">data retention policies</a>.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="https://help.dialpad.com/v1/docs/en/sms-registration-guide" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Learn more about registering your SMS campaign.</a>.</li>
<li class="d-docsite--list-element">Dialpad retains data until it is deleted, an account is canceled, or a <a href="https://help.dialpad.com/v1/docs/en/data-retention-policy" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">retention policies</a> is set up. <a href="https://help.dialpad.com/v1/docs/en/dialpad-cancellation-and-refund-faqs" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Canceled accounts</a> are deleted after 180 days.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="confirmations" tabindex="-1" class="d-docsite--header-2">Confirmations <a class="header-anchor d-link d-docsite--link d-link" href="#confirmations" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Confirmations are provided for actions which can’t be undone or are difficult to undo. These messages should:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Always give users an option to confirm or cancel the action</li>
<li class="d-docsite--list-element">Be used for single, primary task</li>
<li class="d-docsite--list-element">Keep body content to a single line and not use more than two calls to action</li>
</ul>
<p class="d-docsite--paragraph">Confirmation titles should:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Ask if users want to continue, using a concise “verb” +  “noun” question</li>
<li class="d-docsite--list-element">Keep it to one sentence and avoid using punctuation except when using question marks</li>
<li class="d-docsite--list-element">Avoid articles (the, a, an) to keep content short and actionable</li>
<li class="d-docsite--list-element">Be written in sentence case</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add fax number</li>
<li class="d-docsite--list-element">Create contact center</li>
<li class="d-docsite--list-element">Delete department?</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add Fax Number</li>
<li class="d-docsite--list-element">Create a New Contact Center</li>
<li class="d-docsite--list-element">Are you sure you want to delete this department?</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Confirmation body content should:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Clearly explain if the action is difficult to undo or irreversible using plain language</li>
<li class="d-docsite--list-element">Be concise: use only one text line. Don’t start sentences with “Are you sure…”</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">This can’t be undone.</li>
<li class="d-docsite--list-element">Deleting a department cannot be undone.</li>
<li class="d-docsite--list-element">Leaving this page will delete all unsaved changes.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Are you sure you want to permanently delete this department? This action cannot be reversed.</li>
<li class="d-docsite--list-element">If you delete a department, you will be unable to access it in the future.</li>
<li class="d-docsite--list-element">If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page?</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Confirmations primary and secondary actions should:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Be clear and predictable: users should be able to anticipate what will happen when they click an action</li>
<li class="d-docsite--list-element">Scannable: avoid unnecessary words and articles such as “the”, “an”, or “a”</li>
</ul>
<p class="d-docsite--paragraph">Since confirmation messages appear in modals, titles and buttons are closer to each other than they normally would appear. Because of this, button text doesn’t have to follow the “verb” + “noun” pattern. Instead, one word calls to action can be used such as “Cancel” or “Delete”.</p>
<h3 id="deletions" tabindex="-1" class="d-docsite--header-3">Deletions <a class="header-anchor d-link d-docsite--link d-link" href="#deletions" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Before users can delete objects like contacts, departments, contact centers, or responses, we present them with a confirmation message that has two calls to action. One to “Cancel” and one to “Delete”. Keep it short and don’t use the “verb” + “noun” button copy approach.</p>
<p class="d-docsite--paragraph">Primary action:</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Delete</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Remove</li>
<li class="d-docsite--list-element">Erase</li>
<li class="d-docsite--list-element">Discard</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Secondary action:</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Cancel</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Discard</li>
</ul>
</template>
</dialtone-usage>
<h3 id="discarding-changes-while-on-a-page" tabindex="-1" class="d-docsite--header-3">Discarding Changes While on a Page <a class="header-anchor d-link d-docsite--link d-link" href="#discarding-changes-while-on-a-page" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Primary action:</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Discard</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Cancel</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Secondary action:</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Keep Editing</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Go Back</li>
<li class="d-docsite--list-element">Cancel</li>
</ul>
</template>
</dialtone-usage>
<h3 id="leaving-a-page-with-unsaved-changes" tabindex="-1" class="d-docsite--header-3">Leaving a Page with Unsaved Changes <a class="header-anchor d-link d-docsite--link d-link" href="#leaving-a-page-with-unsaved-changes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Primary action:</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Leave Page</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Exit</li>
<li class="d-docsite--list-element">Delete changes</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Secondary action:</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Stay</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Go Back</li>
<li class="d-docsite--list-element">Cancel</li>
<li class="d-docsite--list-element">Discard</li>
<li class="d-docsite--list-element">Keep Editing</li>
</ul>
</template>
</dialtone-usage>
<h2 id="directional-language" tabindex="-1" class="d-docsite--header-2">Directional Language <a class="header-anchor d-link d-docsite--link d-link" href="#directional-language" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Just as links should never state “click here”, avoid using directional language such as “above” or “right”. This language is confusing and unhelpful when picked up by screen readers. It creates challenges for internationalization and can conflict with mobile-friendly designs.</p>
<p class="d-docsite--paragraph">The use of directional language is often an indication of poor visual or content hierarchy. Whenever possible, keep instructional copy and related actions close together so directional language isn’t needed.</p>
<h2 id="save-vs-done" tabindex="-1" class="d-docsite--header-2">Save vs. Done <a class="header-anchor d-link d-docsite--link d-link" href="#save-vs-done" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use “Save” when the user is making an immediate change and “Done” for <a href="#deferred-saves" class="d-docsite--link d-link">deferred saves</a>. Examples for when to use “Save” are when updating a contact, editing a channel’s description, or changing settings. Use “Done” when confirming changes inside a modal or popover that will be applied to the current page, but those updates won’t save to the database.</p>
<h3 id="saving-in-modals-and-sheets" tabindex="-1" class="d-docsite--header-3">Saving in Modals and Sheets <a class="header-anchor d-link d-docsite--link d-link" href="#saving-in-modals-and-sheets" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use “Save” in modals and popovers when the user’s changes are saved directly to the database.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows Save option at the bottom of the modal" src="/assets/images/content/saving-modal@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Exit</li>
<li class="d-docsite--list-element">Done</li>
<li class="d-docsite--list-element">Apply</li>
</ul>
</template>
</dialtone-usage>
<h3 id="saving-at-the-bottom-of-a-page" tabindex="-1" class="d-docsite--header-3">Saving at the Bottom of a Page <a class="header-anchor d-link d-docsite--link d-link" href="#saving-at-the-bottom-of-a-page" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the “Save” + “noun” content formula for save button actions at the bottom of a page when you don’t have the provided context of a modal or compose bar.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows Save option at the bottom of the modal" src="/assets/images/content/saving-bottom-page@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Create discount</li>
<li class="d-docsite--list-element">Save</li>
<li class="d-docsite--list-element">Done</li>
<li class="d-docsite--list-element">Apply</li>
</ul>
</template>
</dialtone-usage>
<h3 id="deferred-saves" tabindex="-1" class="d-docsite--header-3">Deferred Saves <a class="header-anchor d-link d-docsite--link d-link" href="#deferred-saves" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Sometimes, when users confirm changes inside a modal or popover, those changes are applied as unsaved changes to the current page. These updates do not immediately save to the database, but require an additional “Save” action. In these instances, do not use the “Save” verb as this call to action would be misleading.</p>
<p class="d-docsite--paragraph">Instead use “Done” here. When the modal or popover closes, the user will be able to see the impact of their changes and then save them.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows correct usage of Done label when updates are not immediately saved to the database" src="/assets/images/content/deferred-save@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add</li>
<li class="d-docsite--list-element">Edit</li>
<li class="d-docsite--list-element">Manage</li>
<li class="d-docsite--list-element">Save</li>
<li class="d-docsite--list-element">Apply</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">When using a datepicker, also use “Done”.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows correct use of Done action in a datepicker" src="/assets/images/content/deferred-save-datepicker@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Select</li>
<li class="d-docsite--list-element">Save</li>
<li class="d-docsite--list-element">Apply</li>
</ul>
</template>
</dialtone-usage>
<h2 id="close-vs-accept" tabindex="-1" class="d-docsite--header-2">Close vs. Accept <a class="header-anchor d-link d-docsite--link d-link" href="#close-vs-accept" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “Close” when users confirm that they’ve finished reading something, but aren’t required to legally accept terms of service before continuing. Don’t use “OK” as that is an exclamation—not an action. When users click “Close”, they’re not saying “OK”—they’re completing a specific action.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows using close instead of accept if no action requires accepting legal terms" src="/assets/images/content/close-accept@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Ok, OK, O.K., Okay</li>
<li class="d-docsite--list-element">Done</li>
<li class="d-docsite--list-element">Accept</li>
<li class="d-docsite--list-element">Continue</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “Accept” when terms of service require legal confirmation before continuing.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Accept</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Ok, OK, O.K., Okay</li>
<li class="d-docsite--list-element">Done</li>
<li class="d-docsite--list-element">Close</li>
<li class="d-docsite--list-element">Continue</li>
</ul>
</template>
</dialtone-usage>
<h2 id="close-vs-cancel" tabindex="-1" class="d-docsite--header-2">Close vs. Cancel <a class="header-anchor d-link d-docsite--link d-link" href="#close-vs-cancel" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use back arrow buttons as a call-to-action for modals and screens when the content is in a “view-only” state. Don’t use “Close” as the call-to-action when users can make changes to the screen, confirm they’ve read something, or accept terms of service (see “<a href="#close-vs-accept" class="d-docsite--link d-link">Close vs. Accept</a>”).</p>
<dialtone-usage>
<template #do>
<img alt="Picture with back button" src="/assets/images/content/back-button@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Cancel</li>
<li class="d-docsite--list-element">Exit</li>
<li class="d-docsite--list-element">OK</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the “Cancel” action as an option for users to back out changes made on a page, modal, or popover. When pressed, changes are automatically discarded. The “Cancel” action is often paired with “Save” and “Done” actions, always being placed on the far left.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows correct position of save and cancel actions in a popover or modal" src="/assets/images/content/deferred-save@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Done</li>
<li class="d-docsite--list-element">Exit</li>
<li class="d-docsite--list-element">Close</li>
</ul>
</template>
</dialtone-usage>
<h2 id="select-vs-choose" tabindex="-1" class="d-docsite--header-2">Select vs. Choose <a class="header-anchor d-link d-docsite--link d-link" href="#select-vs-choose" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “select” when:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Picking from a limited number of similar options</li>
<li class="d-docsite--list-element">Making an easy or obvious decision that doesn’t require deep reflection or analysis</li>
<li class="d-docsite--list-element">Presenting defined lists and dropdown menus</li>
<li class="d-docsite--list-element">Picking from existing objects, such as departments</li>
</ul>
<p class="d-docsite--paragraph">Pair “Select” modals and screens with the “Done” action.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Select people</li>
<li class="d-docsite--list-element">Select your timezone</li>
<li class="d-docsite--list-element">Select department</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Choose timezone</li>
<li class="d-docsite--list-element">Select a pricing plan.</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “choose” when:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Encouraging users to make a decision that is more subjective, strategic, emotional, or open-ended</li>
<li class="d-docsite--list-element">Picking from a large inventory of items or options that require strategic decision making, such as pricing plans</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Choose a pricing plan</li>
<li class="d-docsite--list-element">Choose a routing option</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Select a routing option</li>
<li class="d-docsite--list-element">Choose a country</li>
<li class="d-docsite--list-element">Pick a pricing plan</li>
</ul>
</template>
</dialtone-usage>
<h2 id="edit-vs-manage" tabindex="-1" class="d-docsite--header-2">Edit vs. Manage <a class="header-anchor d-link d-docsite--link d-link" href="#edit-vs-manage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “edit” when you can change the value of an input field (letters, numbers, properties). Place the action next to the field or area that is editable. There’s no need for a noun in this instance unless it’s unclear what’s being edited.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows contact card with edit action" src="/assets/images/content/edit-content@2x.png" class="d-w100p" />
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Edit a Real-Time Assist card</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use “Edit” if multiple actions can be taken. Use “Manage” instead.</li>
<li class="d-docsite--list-element">Modify</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “manage” when multiple actions can be done or sections and settings will be updated at the same time. Pair this verb with a noun if it’s in a button or it’s unclear what is being managed.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Manage notifications</li>
<li class="d-docsite--list-element">Manage multiple items</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Edit</li>
<li class="d-docsite--list-element">Modify</li>
</ul>
</template>
</dialtone-usage>
<h2 id="change-vs-switch" tabindex="-1" class="d-docsite--header-2">Change vs. Switch <a class="header-anchor d-link d-docsite--link d-link" href="#change-vs-switch" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “change” when users can replace an option, but not edit it. This is most common when changing an image or app theme. This action doesn’t include the ability to edit the properties. There’s no need for a noun unless it’s unclear what’s being changed.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Change</li>
<li class="d-docsite--list-element">Change background</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">TBA</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “switch” when it’s important for users to know what they’re switching between like users, accounts, phone numbers, or modes. When the switch happens, the previous option is turned off, logged out, or deactivated. Always pair “switch” with a noun in titles to prevent confusion.</p>
<dialtone-usage>
<template #do>
<img alt="Picture shows Save option at the bottom of the modal" src="/assets/images/content/switch-accounts@2x.png" class="d-w100p" />
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Switch phone number</li>
<li class="d-docsite--list-element">Switch account</li>
</ul>
</template>
</dialtone-usage>
<h2 id="create-vs-add" tabindex="-1" class="d-docsite--header-2">Create vs. Add <a class="header-anchor d-link d-docsite--link d-link" href="#create-vs-add" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “create” when encouraging users to generate something from scratch, like an office.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Create Router</li>
<li class="d-docsite--list-element">Create Office</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add router</li>
<li class="d-docsite--list-element">Add office</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “add” when encouraging users to extend something that already exists, like a response.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add Contact</li>
<li class="d-docsite--list-element">Add Response</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Create Contact</li>
<li class="d-docsite--list-element">Create Response</li>
</ul>
</template>
</dialtone-usage>
<h2 id="view-vs-see" tabindex="-1" class="d-docsite--header-2">View vs. See <a class="header-anchor d-link d-docsite--link d-link" href="#view-vs-see" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “view” when encouraging users to visit a specific page or section for more information, or to reveal more information. Use “view” in buttons, calls to action, and link text.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">View all { x }</li>
<li class="d-docsite--list-element">View call summary</li>
<li class="d-docsite--list-element">View analytics</li>
<li class="d-docsite--list-element">Try clearing your filters to view all results</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">See all { x }</li>
<li class="d-docsite--list-element">See call summary</li>
<li class="d-docsite--list-element">See analytics</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “see” in more general, conversational descriptions without a specific call to action.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Create your first chatbot and see how it looks.</li>
<li class="d-docsite--list-element">Agents will see these questions during the call.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Create your first chatbot and view how it looks.</li>
<li class="d-docsite--list-element">Agents will view these questions during the call.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="need-vs-must" tabindex="-1" class="d-docsite--header-2">Need vs. Must <a class="header-anchor d-link d-docsite--link d-link" href="#need-vs-must" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “need” when telling users they’re required to do or should do something.</p>
<dialtone-usage>
<template #do>
<p class="d-docsite--paragraph">To allow agents time to wrap-up between calls, you need to set a wrap-up time allowance.</p>
</template>
<template #dont>
<p class="d-docsite--paragraph">To allow agents time to wrap-up between calls, you must set a wrap-up time allowance.</p>
</template>
</dialtone-usage>
<h2 id="export-vs-download" tabindex="-1" class="d-docsite--header-2">Export vs. Download <a class="header-anchor d-link d-docsite--link d-link" href="#export-vs-download" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “export” when a user needs to transfer data from Dialpad and convert it into a different format.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Export CSV file</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Download CSV file</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “download” as a call to action when users need to copy data (of the same format) from Dialpad to another system.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Download recording</li>
<li class="d-docsite--list-element">Download chat history</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Export recording</li>
<li class="d-docsite--list-element">Export chat history</li>
</ul>
</template>
</dialtone-usage>
<h2 id="import-vs-upload" tabindex="-1" class="d-docsite--header-2">Import vs. Upload <a class="header-anchor d-link d-docsite--link d-link" href="#import-vs-upload" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the verb “import” when a user needs to transfer data and convert it into a different format so it can be used in Dialpad.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Import CSV file</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Upload CSV file</li>
</ul>
</template>
</dialtone-usage>
<p class="d-docsite--paragraph">Use the verb “upload” as a call to action when users need to copy data (of the same format) into Dialpad.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Upload image</li>
<li class="d-docsite--list-element">Upload file</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Upload CSV file</li>
<li class="d-docsite--list-element">Import image</li>
</ul>
</template>
</dialtone-usage>
</div></template>


